import { Popup } from "devextreme-react/popup";
import { useEffect, useRef, useState } from "react";
import ItiButton from "../ItiButton/ItiButton";
import "./ItiPopUp.scss";

interface ItiPopUpProps {
	title?: string;
	visible?: boolean;
	children?: any;
	titleIcon?: string;
	cssTitle?: string;
	width?: number | string;
	maxWidth?: number | string;
	maxHeight?: number | string;
	minHeight?: number | string;
	height?: number | string;
	showCloseButton?: boolean;
	wrapperAttr?: any;
	onHide?: () => void;
	onShowing?: () => void;
	headerOptions?: Array<{
		text: string;
		icon: string;
		onClick: () => void;
	}>;
	idMenuOptions?: string;
}

export default function ItiPopUpComponent({
	children,
	visible,
	width,
	maxWidth,
	titleIcon,
	cssTitle,
	height = "auto",
	maxHeight = "100%",
	minHeight,
	title,
	showCloseButton,
	wrapperAttr,
	onHide,
	onShowing,
	headerOptions = [],
	idMenuOptions = "popup-menu-options",
}: Readonly<ItiPopUpProps>) {
	const [menuVisible, setMenuVisible] = useState(false);
	const [actionButtonVisible, setActionButtonVisible] = useState(true);
	const dropdownRef = useRef<HTMLDivElement>(null);

	useEffect(() => {
		const handleClickOutside = (event: MouseEvent) => {
			if (
				dropdownRef.current &&
				!dropdownRef.current.contains(event.target as Node)
			) {
				setMenuVisible(false);
			}
		};

		if (menuVisible) {
			document.addEventListener("mousedown", handleClickOutside);
		}

		return () => {
			document.removeEventListener("mousedown", handleClickOutside);
		};
	}, [menuVisible]);

	useEffect(() => {
		if (headerOptions.length === 1) {
			setActionButtonVisible(true);
		}
	}, [headerOptions]);

	const handleOptionsButtonClick = (e: any) => {
		if (e?.event) {
			e.event.stopPropagation();
			e.event.preventDefault();
		}

		setMenuVisible(!menuVisible);
	};

	const handleMenuItemClick = (option: any) => {
		setMenuVisible(false);
		if (option.onClick) {
			option.onClick();
		}
	};

	const handleSingleActionClick = (option: any) => {
		setActionButtonVisible(false);
		if (option.onClick) {
			option.onClick();
		}
	};

	const handleCloseClick = () => {
		if (onHide) {
			onHide();
		}
	};

	const titleRender = () => {
		if (headerOptions.length === 0) {
			return (
				<div
					className="d-flex align-items-center justify-content-between w-100"
					style={{ height: "74px", minHeight: "74px" }}
				>
					<div className="d-flex align-items-center popup-title-wrapper">
						{titleIcon && <i className={`${titleIcon}`} />}
						<span
							className={
								cssTitle
									? `${cssTitle} popup-header-title`
									: "popup-header-title"
							}
						>
							{title}
						</span>
					</div>
					<button
						className="popup-close-button"
						onClick={handleCloseClick}
						type="button"
					>
						<i className="mdi mdi-close" />
					</button>
				</div>
			);
		}

		if (headerOptions.length === 1) {
			// Si solo hay una opción, mostrar botón directo que ejecute la acción inmediatamente
			const singleOption = headerOptions[0];
			return (
				<div
					className="d-flex align-items-center justify-content-between w-100"
					style={{ height: "74px", minHeight: "74px" }}
				>
					<span className="popup-header-title">{title}</span>
					<div className="d-flex align-items-center gap-2 popup-header-actions">
						{actionButtonVisible && (
							<ItiButton
								text={singleOption.text}
								icon={singleOption.icon}
								elementAttr={{ class: "btn-sm" }}
								onClick={() => handleSingleActionClick(singleOption)}
							/>
						)}
						<button
							className="popup-close-button"
							onClick={handleCloseClick}
							type="button"
						>
							<i className="mdi mdi-close" />
						</button>
					</div>
				</div>
			);
		}

		// Si hay múltiples opciones, mostrar menú de tres puntos con dropdown personalizado
		return (
			<div
				className="d-flex align-items-center justify-content-between w-100"
				style={{ height: "74px", minHeight: "74px" }}
			>
				<span className="popup-header-title">{title}</span>
				<div className="d-flex align-items-center gap-2">
					<div
						className="popup-header-dropdown"
						ref={dropdownRef}
						style={{ position: "relative" }}
					>
						<ItiButton
							icon="mdi mdi-dots-horizontal"
							elementAttr={{
								class: "btn-sm",
								id: idMenuOptions,
							}}
							onClick={handleOptionsButtonClick}
						/>
						{menuVisible && (
							<div className="popup-dropdown-menu">
								{headerOptions.map((option, index) => (
									<button
										key={index}
										className="popup-dropdown-item"
										type="button"
										onClick={() => handleMenuItemClick(option)}
									>
										{option.icon && <i className={`${option.icon} me-2`} />}
										<span>{option.text}</span>
									</button>
								))}
							</div>
						)}
					</div>
					<button
						className="popup-close-button"
						onClick={handleCloseClick}
						type="button"
					>
						<i className="mdi mdi-close" />
					</button>
				</div>
			</div>
		);
	};

	return (
		<Popup
			visible={visible}
			dragEnabled={false}
			hideOnOutsideClick={false}
			showCloseButton={
				headerOptions.length === 0 ? (showCloseButton ?? true) : false
			}
			showTitle={true}
			titleRender={titleRender}
			title={undefined}
			className={"popup-container"}
			wrapperAttr={wrapperAttr}
			width={width ?? "75%"}
			height={height ?? "100%"}
			maxHeight={maxHeight}
			minHeight={minHeight}
			maxWidth={maxWidth ?? 1600}
			onShowing={() => {
				if (onShowing) {
					onShowing();
				}
			}}
			onHidden={() => {
				if (onHide) {
					onHide();
				}
			}}
		>
			{children}
		</Popup>
	);
}
